import {
  getCollectPostsList,
  getMyPostsList,
  getPostList,
  getPraisePostsList,
} from '@/api/forum';

const PAGE_SIZE = 20;
const CURSOR_PAGE_SIZE = 18;

export const extractForumRecords = (raw) => {
  if (Array.isArray(raw)) return raw;
  const data = raw || {};
  return data.list || data.records || data.rows || [];
};

const getPostId = (post) => post && (post.postsId || post.postId || post.id);

const getPageValue = (data, fallback) =>
  Number(data.currentPage || data.current || data.page || data.pageNo || data.pageNum || fallback);

const getNextPage = (data, fallback) => {
  const nextPage = Number(data.nextPage || data.nextPageNo || 0);
  if (nextPage > 0) return nextPage;
  return getPageValue(data, fallback) + 1;
};

const getTotalCount = (data) => Number(data.total || data.totalCount || data.count || 0);

const getTotalPages = (data) => Number(data.pages || data.totalPage || data.totalPages || 0);

const getMetaFinished = (data, currentLength, page) => {
  if (!data || Array.isArray(data)) return null;
  if (typeof data.hasNext === 'boolean') return !data.hasNext;
  if (typeof data.hasMore === 'boolean') return !data.hasMore;
  if (typeof data.isLast === 'boolean') return data.isLast;
  if (typeof data.last === 'boolean') return data.last;
  const total = getTotalCount(data);
  if (total > 0 && currentLength >= total) return true;
  const totalPages = getTotalPages(data);
  if (totalPages > 0 && getPageValue(data, page) >= totalPages) return true;
  return null;
};

const getNextLastPostId = (data, records, fallback) => {
  if (data && !Array.isArray(data)) {
    const cursor = data.nextLastPostId || data.nextCursor || data.cursor;
    if (cursor !== undefined && cursor !== null) return cursor;
  }
  const lastRecord = records[records.length - 1];
  const recordId = getPostId(lastRecord);
  if (recordId !== undefined && recordId !== null) return recordId;
  if (data && !Array.isArray(data)) {
    const cursor = data.lastPostId || data.lastId;
    if (cursor !== undefined && cursor !== null) return cursor;
  }
  return fallback;
};

const appendUniqueRecords = (list, records) => {
  const existed = new Set(list.map((item) => String(getPostId(item))));
  const nextRecords = records.filter((item) => {
    const id = getPostId(item);
    if (id === undefined || id === null) return true;
    const key = String(id);
    if (existed.has(key)) return false;
    existed.add(key);
    return true;
  });
  list.push(...nextRecords);
  return nextRecords.length;
};

const resolveProfileFetcher = (mode) => {
  if (mode === 'mine') return getMyPostsList;
  if (mode === 'praise') return getPraisePostsList;
  if (mode === 'collect') return getCollectPostsList;
  return null;
};

const getProfileListParams = (mode, state) => {
  if (mode === 'mine') {
    return {
      page: state.page,
      size: PAGE_SIZE,
    };
  }
  return { lastPostId: state.lastPostId };
};

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const resolveCursorFetcher = (mode, options = {}) => {
  return (id) => {
    const params = options.getGroupParams && options.getGroupParams();
    if (!params || !hasValue(params.groupId) || !hasValue(params.tagId)) return Promise.resolve({ data: [] });
    return getPostList({
      groupId: params.groupId,
      tagId: params.tagId,
      sortType: params.sortType,
      lastPostId: id,
    });
  };
};

export const createForumPostList = (mode, options = {}) => ({
  list: [],
  loading: false,
  fetching: false,
  finished: false,
  page: 1,
  lastPostId: 0,
  async load(reset = false) {
    if (this.fetching) return;
    if (reset) {
      this.list = [];
      this.page = 1;
      this.lastPostId = 0;
      this.finished = false;
    }
    if (this.finished) {
      this.loading = false;
      return;
    }
    this.fetching = true;
    this.loading = true;
    try {
      let records = [];
      const profileFetcher = resolveProfileFetcher(mode);
      if (profileFetcher) {
        const currentLast = this.lastPostId;
        const res = await profileFetcher(getProfileListParams(mode, this));
        const data = res && res.data;
        records = extractForumRecords(data);
        const appendedCount = appendUniqueRecords(this.list, records);
        const nextLast = getNextLastPostId(data, records, currentLast);
        const metaFinished = getMetaFinished(data, this.list.length, this.page);
        this.page = getNextPage(data || {}, this.page);
        if (String(nextLast) !== String(currentLast)) this.lastPostId = nextLast;
        if (!records.length || metaFinished === true || appendedCount === 0) {
          this.finished = true;
        }
      } else {
        const currentLast = this.lastPostId;
        const res = await resolveCursorFetcher(mode, options)(currentLast);
        records = extractForumRecords(res && res.data);
        if (!records.length) {
          this.finished = true;
        } else {
          const nextLast = records[records.length - 1] && records[records.length - 1].postsId;
          if (nextLast === undefined || String(nextLast) === String(currentLast)) {
            this.finished = true;
          } else {
            this.lastPostId = nextLast;
          }
          if (records.length < CURSOR_PAGE_SIZE) this.finished = true;
        }
        this.list.push(...records);
      }
    } catch (err) {
      console.error('[forumPostList] load failed:', mode, err);
      this.finished = true;
    } finally {
      this.fetching = false;
      this.loading = false;
    }
  },
  reset() {
    return this.load(true);
  },
});
